import { Head } from "vite-react-ssg";
import type { Lang } from "../i18n/lang";
import { LANGS, localizedPath } from "../i18n/lang";
import { SITE } from "../config/site";
import {
  SITE_URL,
  ogImage,
  OG_IMAGE_TYPE,
  SLOGAN,
  OG_IMAGE_W,
  OG_IMAGE_H,
  OG_LOCALE,
  ROUTE_META,
  type RoutePath,
} from "../config/seo";

// Per-route document head. vite-react-ssg's <Head> injects these into <head> at prerender time,
// so every static HTML file ships correct title/description/canonical/OG/hreflang and <html lang>.
export function Seo({ path, lang }: { path: RoutePath; lang: Lang }) {
  const meta = ROUTE_META[path][lang];
  const ogTitle = meta.ogTitle ?? meta.title;
  const image = ogImage(lang);
  const canonical = `${SITE_URL}${localizedPath(path, lang)}`;
  const xDefault = `${SITE_URL}${localizedPath(path, "uz")}`;
  const otherLang: Lang = lang === "uz" ? "ru" : "uz";

  return (
    <Head>
      <html lang={lang} />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />

      {/* hreflang — every language variant of this route + x-default (uz). */}
      {LANGS.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${SITE_URL}${localizedPath(path, l)}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={xDefault} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:type" content={OG_IMAGE_TYPE} />
      <meta property="og:image:width" content={String(OG_IMAGE_W)} />
      <meta property="og:image:height" content={String(OG_IMAGE_H)} />
      <meta property="og:image:alt" content={SLOGAN[lang]} />
      <meta property="og:locale" content={OG_LOCALE[lang]} />
      <meta property="og:locale:alternate" content={OG_LOCALE[otherLang]} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={SLOGAN[lang]} />
    </Head>
  );
}
